import { z } from 'zod';
import { Condition, evaluate } from './conditions.js';
import { Industry, INDUSTRIES } from './controls.js';
import { OutsideInDomain, outsideInConsentText } from './outside-in.js';
/**
 * Submission links (task 68, decision 0044). An insurer or broker shares a public link in its own name
 * and colour; a business fills in a short triage form; first-match tier rules pick how deep its check
 * goes; staff accept it (a client record, an assessment on the tier's journey, and an invitation for the
 * owner) or reject it with a note.
 */
export const INTAKE_TIERS = ['quick', 'standard', 'full'];
export const TIER_LABEL = {
    quick: 'Quick check (about 3 minutes)',
    standard: 'Standard check',
    full: 'Full check with industry pack',
};
// —— the triage form: fixed, so rules and the client record can rely on it ——
export const EMPLOYEE_BANDS = ['1-9', '10-49', '50-199', '200+'];
export const REVENUE_BANDS = ['under-1m', '1m-10m', '10m-50m', '50m+'];
export const REVENUE_LABEL = {
    'under-1m': 'Under RM 1 million',
    '1m-10m': 'RM 1–10 million',
    '10m-50m': 'RM 10–50 million',
    '50m+': 'Over RM 50 million',
};
/** The activities insurers treat as higher risk, asked when a link prepares proposal forms (task 90). */
export const HIGHER_RISK_ACTIVITIES = {
    social_media: 'Runs a social network or social media platform',
    adult_gambling: 'Adult entertainment or gambling',
    hosting: 'Internet service, hosting, data centre, data processing or cyber security services for others',
    mining: 'Mining',
    digital_assets: 'Cryptocurrency, NFTs, digital assets, decentralised finance or peer-to-peer lending',
    online_only: 'Sells only online: all revenue comes from e-commerce',
    none: 'None of these',
};
/** A revenue band as a range in RM, [from, under), with null for no upper end. */
export const REVENUE_RANGE = {
    'under-1m': [0, 1_000_000],
    '1m-10m': [1_000_000, 10_000_000],
    '10m-50m': [10_000_000, 50_000_000],
    '50m+': [50_000_000, null],
};
/** The band an amount falls in. */
export const revenueBand = (rm) => REVENUE_BANDS.find((b) => REVENUE_RANGE[b][1] === null || rm < REVENUE_RANGE[b][1]);
const YesNo = z.enum(['yes', 'no']);
const Phone = z
    .string()
    .trim()
    .max(30)
    .regex(/^[+\d][\d\s-]{5,}$/, 'A phone number, e.g. +60 12-345 6789');
const Website = z
    .string()
    .trim()
    .max(200)
    .regex(/^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i, 'A web address, e.g. example.com.my');
export const TriageAnswers = z.strictObject({
    business_name: z.string().trim().min(2, 'Give the business’s name').max(200),
    contact_name: z.string().trim().min(2, 'Give your name').max(120),
    contact_email: z.string().trim().toLowerCase().email('An email address we can reach you at').max(320),
    phone: Phone.optional(),
    website: Website.optional(),
    industry: Industry,
    country: z.string().trim().length(2).toUpperCase().default('MY'),
    employees: z.enum(EMPLOYEE_BANDS),
    revenue: z.enum(REVENUE_BANDS),
    card_payments: YesNo,
    personal_data: YesNo,
    sum_insured: z.number().min(0).max(1e10).optional(),
    renewal_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date')
        .optional(),
    notes: z.string().trim().max(1000).optional(),
    // Asked when the link prepares insurers' proposal forms (task 90): what picks the forms.
    revenue_amount: z.number().min(0).max(100_000_000_000).optional(),
    overseas_revenue: YesNo.optional(),
    activities: z
        .array(z.enum(Object.keys(HIGHER_RISK_ACTIVITIES)))
        .max(7)
        .refine((a) => !a.includes('none') || a.length === 1, '“None of these” goes alone')
        .optional(),
});
/** What a rule can test, with how the form asks it; rules use these as facts. */
export const TRIAGE_FACTS = {
    industry: {
        label: 'Industry',
        options: Object.entries(INDUSTRIES).map(([v, l]) => [v, String(l)]),
    },
    country: { label: 'Country', options: [['MY', 'Malaysia'], ['SG', 'Singapore'], ['ID', 'Indonesia'], ['TH', 'Thailand']] }, // prettier-ignore
    employees: { label: 'Employees', options: EMPLOYEE_BANDS.map((b) => [b, b]) },
    revenue: { label: 'Annual revenue', options: REVENUE_BANDS.map((b) => [b, REVENUE_LABEL[b]]) },
    card_payments: {
        label: 'Takes card payments',
        options: [
            ['yes', 'Yes'],
            ['no', 'No'],
        ],
    },
    personal_data: {
        label: 'Holds customers’ personal data',
        options: [
            ['yes', 'Yes'],
            ['no', 'No'],
        ],
    },
    sum_insured: { label: 'Sum insured wanted (RM)', options: null },
};
// —— links and their tier rules ——
export const TierRule = z.strictObject({
    name: z.string().trim().min(1).max(80),
    when: Condition,
    tier: z.enum(INTAKE_TIERS),
});
const Hex = z.string().regex(/^#[0-9a-f]{6}$/i, 'A colour like #0f5c4c');
export const IntakeLinkInput = z
    .strictObject({
    name: z.string().trim().min(1).max(80),
    /** Shown as the form's title, e.g. "Cyber insurance: tell us about your business". */
    heading: z.string().trim().min(1).max(120),
    intro: z.string().trim().max(1000).default(''),
    /** The insurer's or broker's name as the business sees it. */
    brand_name: z.string().trim().min(1).max(120),
    accent: Hex.default('#0f5c4c'),
    active: z.boolean().default(true),
    rules: z.array(TierRule).max(20).default([]),
    default_tier: z.enum(INTAKE_TIERS).default('standard'),
    /** The journey each tier's assessment follows; null lets the tenant's configuration choose. */
    journeys: z.partialRecord(z.enum(INTAKE_TIERS), z.string().max(64).nullable()).default({}),
    /**
     * Prepares insurers' proposal forms (task 90): the form also asks revenue, overseas revenue and
     * higher-risk activities, and accepting proposes the forms the client fits.
     */
    placement: z.boolean().default(false),
})
    .superRefine((l, ctx) => {
    l.rules.forEach((r, i) => {
        for (const ref of factRefs(r.when))
            if (!(ref in TRIAGE_FACTS))
                ctx.addIssue({ code: 'custom', path: ['rules', i, 'when'], message: `Rules can test ${Object.keys(TRIAGE_FACTS).join(', ')}; not ${ref}` }); // prettier-ignore
    });
});
/**
 * Ready-made links (task 112, decision 0074): a new link starts from one, so nobody builds tier rules from
 * nothing. "Standard SME" is the recommended start: the full check for larger businesses, the quick one
 * for the smallest that take no card payments, the standard one otherwise. Every part stays editable.
 */
export const LINK_TEMPLATES = {
    standard_sme: {
        label: 'Standard SME',
        blurb: 'Full check for larger businesses, quick for the smallest, standard for the rest.',
        heading: 'Cyber insurance: tell us about your business',
        placement: false,
    },
    placement: {
        label: 'Proposal forms',
        blurb: 'As Standard SME, and it prepares each insurer’s proposal form the client fits.',
        heading: 'Cyber insurance quotes: tell us about your business',
        placement: true,
    },
    blank: {
        label: 'Start from blank',
        blurb: 'No rules: every business gets the standard check until you add some.',
        heading: 'Cyber insurance: tell us about your business',
        placement: false,
    },
};
const SME_RULES = [
    {
        name: 'Larger businesses',
        tier: 'full',
        when: { fact: 'employees', op: 'in', value: ['50-199', '200+'] },
    },
    { name: 'Higher revenue', tier: 'full', when: { fact: 'revenue', op: 'in', value: ['10m-50m', '50m+'] } },
    {
        name: 'Smallest, no card payments',
        tier: 'quick',
        when: {
            all: [
                { fact: 'employees', op: 'in', value: ['1-9'] },
                { fact: 'card_payments', op: 'in', value: ['no'] },
            ],
        },
    },
];
/** What a new link starts with, from a template: its rules, default check, heading and placement. */
export function linkTemplate(t) {
    const d = LINK_TEMPLATES[t];
    return {
        heading: d.heading,
        placement: d.placement,
        default_tier: 'standard',
        rules: t === 'blank' ? [] : SME_RULES.map((r) => ({ ...r })),
    };
}
export const IntakeLinkPatch = z.strictObject({
    revision: z.number().int().min(1),
    name: z.string().trim().min(1).max(80).optional(),
    heading: z.string().trim().min(1).max(120).optional(),
    intro: z.string().trim().max(1000).optional(),
    brand_name: z.string().trim().min(1).max(120).optional(),
    accent: Hex.optional(),
    active: z.boolean().optional(),
    rules: z.array(TierRule).max(20).optional(),
    default_tier: z.enum(INTAKE_TIERS).optional(),
    journeys: z.partialRecord(z.enum(INTAKE_TIERS), z.string().max(64).nullable()).optional(),
    placement: z.boolean().optional(),
});
/** The facts a rule can test, from triage answers. Note: missing answers make a test false. */
export const triageFacts = (a) => Object.fromEntries(Object.keys(TRIAGE_FACTS).map((k) => [k, a[k]]));
/** The first rule that matches picks the tier; none does, the default. */
export function pickTier(rules, fallback, answers) {
    const facts = triageFacts(answers);
    const hit = rules.find((r) => evaluate(r.when, { answers: {}, facts }));
    return hit ? { tier: hit.tier, rule: hit.name } : { tier: fallback, rule: null };
}
/** Facts a condition tests. */
export function factRefs(c) {
    if ('all' in c)
        return c.all.flatMap(factRefs);
    if ('any' in c)
        return c.any.flatMap(factRefs);
    if ('not' in c)
        return factRefs(c.not);
    return 'fact' in c ? [c.fact] : [`question ${c.question}`];
}
// —— submissions ——
export const SUBMISSION_STATUSES = ['new', 'accepted', 'rejected'];
/** The public form's body; `check_domain` is the business agreeing to a first view of its website's domain (task 73). */
export const IntakeSubmit = z.strictObject({
    answers: TriageAnswers,
    check_domain: z.boolean().optional(),
    /** A field people never see: a form that fills it is a bot. */
    company_fax: z.string().max(200).optional(),
});
/** The domain a website answer names: its host, lower-case, without `www.`; null when it names none. */
export function websiteDomain(website) {
    if (!website)
        return null;
    const host = website
        .trim()
        .replace(/^https?:\/\//i, '')
        .split(/[/?#]/)[0]
        .toLowerCase()
        .replace(/^www\./, '');
    const d = OutsideInDomain.safeParse(host);
    return d.success ? d.data : null;
}
/** The sentence the form asks the business to agree to: the same as the Connections page's (decision 0052). */
export const intakeConsentText = (p) => outsideInConsentText({ business: p.business, insurer: p.brand, domains: [p.domain] });
export const AcceptSubmission = z.strictObject({
    /** Overrides the tier the rules picked. */
    tier: z.enum(INTAKE_TIERS).optional(),
    /** The client key to use; by default one made from the business name. */
    customer_key: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9][A-Za-z0-9 _.&'()-]*$/)
        .max(200)
        .optional(),
});
export const RejectSubmission = z.strictObject({
    /** Sent to the business: say why, kindly. */
    note: z.string().trim().min(1, 'Say why, for the business').max(1000),
});
/** Dry run: these rules over the link's recent submissions (or the samples, when it has none). */
export const IntakeDryRun = z.strictObject({
    rules: z.array(TierRule).max(20),
    default_tier: z.enum(INTAKE_TIERS),
});
/** Made-up businesses to try rules on before a link has submissions. */
export const SAMPLE_TRIAGE = [
    { label: 'Accounting practice, 6 staff', industry: 'professional_services', employees: '1-9', revenue: 'under-1m', card_payments: 'no', personal_data: 'yes', country: 'MY' }, // prettier-ignore
    { label: 'Machine shop, 40 staff', industry: 'manufacturing', employees: '10-49', revenue: '1m-10m', card_payments: 'no', personal_data: 'no', country: 'MY' }, // prettier-ignore
    { label: 'Clinic group, 120 staff, patient records', industry: 'healthcare', employees: '50-199', revenue: '10m-50m', card_payments: 'yes', personal_data: 'yes', country: 'MY' }, // prettier-ignore
    { label: 'Online retailer, 25 staff', industry: 'retail_ecommerce', employees: '10-49', revenue: '10m-50m', card_payments: 'yes', personal_data: 'yes', country: 'MY' }, // prettier-ignore
    { label: 'Logistics firm, 300 staff', industry: 'logistics', employees: '200+', revenue: '50m+', card_payments: 'no', personal_data: 'yes', country: 'MY' }, // prettier-ignore
];
/** A client key from a business name: lower-case words joined by hyphens. */
export const keyFromName = (name) => name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'client'; // prettier-ignore
//# sourceMappingURL=intake.js.map